import React from 'react';
import {
  Box,
  Title,
  Paper,
  Grid,
  Text,
  Container,
  Group,
  Stack
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react'; // USING CHECKMARK ICON

const InvoiceTemplate = ({ invoice }) => {
  if (!invoice) return null;

  const residentName = invoice.resident 
    ? `${invoice.resident.firstName} ${invoice.resident.lastName}` 
    : invoice.manualName || 'Guest / Unknown';
    
  const residentAddress = invoice.resident 
    ? `Blk ${invoice.resident.address.block} Lot ${invoice.resident.address.lot} ${invoice.resident.address.street}`
    : '';

  const formatMoney = (amount) => {
    return Number(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const invoiceDate = invoice.createdAt ? new Date(invoice.createdAt) : new Date();
  const day = invoiceDate.getDate();
  const month = invoiceDate.toLocaleString('default', { month: 'long' });
  const year = invoiceDate.getFullYear().toString().slice(-2);

  const duesItems = invoice.items ? invoice.items.filter(i => i.type === 'monthly_dues') : [];
  const otherItems = invoice.items ? invoice.items.filter(i => i.type !== 'monthly_dues') : [];

  // Normalize payment method for comparison
  const paymentMethod = (invoice.paymentMethod || '').toLowerCase();

  const renderRows = (items, minRows) => {
    const rows = [];
    const count = Math.max(items.length, minRows);

    for (let i = 0; i < count; i++) {
        const item = items[i];
        rows.push(
            <Grid key={i} gutter={0} align="flex-end" mb={4}>
                <Grid.Col span={9} style={{ paddingRight: '15px' }}>
                    <Box style={{ borderBottom: '1px solid black', paddingLeft: '5px', height: '24px', display: 'flex', alignItems: 'flex-end' }}>
                        <Text size="sm" lineClamp={1} style={{ width: '100%', lineHeight: '1.4', marginBottom: '-2px' }}>
                            {item ? item.description : '\u00A0'}
                        </Text>
                    </Box>
                </Grid.Col>
                <Grid.Col span={3}>
                    <Box style={{ borderBottom: '1px solid black', textAlign: 'right', paddingRight: '5px', height: '24px', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        <Text size="sm" style={{ lineHeight: '1.4', marginBottom: '-2px' }}>
                            {item ? `₱ ${formatMoney(item.amount)}` : '\u00A0'}
                        </Text>
                    </Box>
                </Grid.Col>
            </Grid>
        );
    }
    return rows;
  };

  const PaymentCheckbox = ({ label, isChecked }) => (
      <Group>
          <Box style={{ 
              width: '20px', 
              height: '20px', 
              border: '1px solid black', 
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
          }}>
              {/* Renders CHECKMARK if true */}
              {isChecked && <IconCheck size={18} color="black" stroke={3} />}
          </Box>
          <Text size="sm" fw={600} style={{ minWidth: '110px' }}>{label}:</Text>
          <Box style={{ borderBottom: '1px solid black', flex: 1 }} />
      </Group>
  );

  return (
    <Box style={{ overflowX: 'auto', width: '100%' }}>
        <Container size="sm" style={{ padding: 0, minWidth: '500px' }}>
        <Paper p="md" style={{ backgroundColor: 'white', fontFamily: 'Arial, sans-serif', color: '#000', border: '1px solid #000' }}>
            
            <Grid gutter={0}>
                <Grid.Col span={2}></Grid.Col>
                <Grid.Col span={8} style={{ textAlign: 'center' }}>
                    <Title order={4} style={{ textTransform: 'uppercase', fontSize: '16px' }}>BANCOM LIFE HOMEOWNERS' ASSOCIATION, INC.</Title>
                    <Text size="xs">(Non Stock Profit Organization)</Text>
                    <Text size="xs">Bancom Plaza, Gulod Malaya, San Mateo, Rizal 1850</Text>
                    <Text size="xs">Tel No. 8706-5135 * Non-VAT Reg. TIN: 237-177-709-0000</Text>
                </Grid.Col>
                <Grid.Col span={2}></Grid.Col>
            </Grid>

            <Group justify="space-between" mt="md" align="flex-end">
                <Title order={3} style={{ textTransform: 'uppercase' }}>SYSTEM RECEIPT</Title>
                <Group gap={5}>
                    <Text fw={600}>Date:</Text>
                    <Box style={{ borderBottom: '1px solid black', width: '150px', textAlign: 'center' }}>
                        {month} {day}, 20{year}
                    </Box>
                </Group>
            </Group>

            <Box mt="md">
                <Group gap={5} style={{ marginBottom: '8px' }}>
                    <Text>RECEIVED from</Text>
                    <Box style={{ borderBottom: '1px solid black', flex: 1, paddingLeft: '10px', fontWeight: 'bold' }}>
                        {residentName}
                    </Box>
                </Group>
                <Group gap={5} style={{ marginBottom: '8px' }}>
                    <Text>with TIN</Text>
                    <Box style={{ borderBottom: '1px solid black', width: '100px', height: '10px' }}></Box>
                    <Text>and address at</Text>
                    <Box style={{ borderBottom: '1px solid black', flex: 1, paddingLeft: '10px'}}>
                        <Text>{invoice.resident ? `${residentAddress} St.` : ''}</Text>
                    </Box>
                </Group>
                <Group gap={5} style={{ marginBottom: '8px' }} justify="flex-end">
                    <Box style={{ borderBottom: '1px solid black', flex: 1, height: '10px'}}></Box>
                    <Text>engaged in the Bus. Style of</Text>
                    <Box style={{ borderBottom: '1px solid black', width: '200px', height: '10px'}}></Box>
                </Group>
                <Group gap={5} style={{ marginBottom: '8px' }}>
                    <Text>the sum of Pesos</Text>
                    <Box style={{ borderBottom: '1px solid black', flex: 1, height: '10px'}}></Box>
                </Group>
                <Group gap={5}>
                    <Text>(PHP</Text>
                    <Box style={{ borderBottom: '1px solid black', width: '150px', textAlign: 'center', fontWeight: 'bold' }}>
                        {formatMoney(invoice.totalAmount)}
                    </Box>
                    <Text>) In partial/Full payment of:</Text>
                </Group>
            </Box>

            <Box mt="lg">
                <Text fw={700} mb={5}>ASSOCIATION DUES:</Text>
                {renderRows(duesItems, 3)}

                <Text fw={700} mt="md" mb={5}>OTHERS:</Text>
                {renderRows(otherItems, 2)}
            </Box>

            <Grid mt="xl" justify="flex-end" gutter="md">
                <Grid.Col span={6}>
                    <Stack gap="xs" mt="lg">
                        <PaymentCheckbox 
                            label="CASH" 
                            isChecked={paymentMethod === 'cash'} 
                        />
                        <PaymentCheckbox 
                            label="GCASH" 
                            isChecked={paymentMethod === 'gcash'} 
                        />
                        <PaymentCheckbox 
                            label="BANK TRANSFER" 
                            isChecked={paymentMethod === 'bank_transfer'} 
                        />
                    </Stack>
                </Grid.Col>
                
                <Grid.Col span={6}>
                    <Stack gap="xs">
                        <Group justify="space-between" mb={4}>
                            <Text fw={700} size="sm">TOTAL AMOUNT DUE</Text>
                            <Text fw={700} style={{ borderBottom: '1px solid black', padding: '0 5px', minWidth: '100px', textAlign: 'right' }}>
                                ₱ {formatMoney(invoice.totalAmount)}
                            </Text>
                        </Group>
                        
                        <Group justify="space-between">
                            <Text fw={700} size="sm">TOTAL AMOUNT PAID</Text>
                            <Text fw={700} style={{ borderBottom: '1px solid black', padding: '0 5px', minWidth: '100px', textAlign: 'right' }}>
                                ₱ {formatMoney(invoice.amountPaid || 0)}
                            </Text>
                        </Group>
                    </Stack>

                    <Box mt="xl">
                        <Text fw={700} size="sm">RECEIVED BY:</Text>
                        <Box style={{ borderBottom: '1px solid black', marginTop: '25px', textAlign: 'center', fontWeight: 'bold' }}>
                            {invoice.receivedBy || 'Admin'}
                        </Box>
                        <Text size="xs" align="center" mt={2}>COLLECTOR</Text>
                    </Box>
                </Grid.Col>
            </Grid>

            <Text size="xs" c="dimmed" align="center" mt="xl" style={{ fontStyle: 'italic' }}>
                Note: Generated on {new Date().toLocaleString('en-PH')}. Please visit the office for further inquiries.
            </Text>

        </Paper>
        </Container>
    </Box>
  );
};

export default InvoiceTemplate;